import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { StringCustomFieldConfig, SharedModule, FormInputComponent, DataService, NotificationService } from '@vendure/admin-ui/core';
import { PaidOptionItem } from '../common/ui-types';
import { safeJSONParse } from '../../common/ui/utils';
import { Asset } from '../../common/ui/generated';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { normalizeString } from '@vendure/common/lib/normalize-string';
import { CurrencyCode } from '@vendure/core';

@Component({
  template: `
    <vdr-card>
      <div class="form-row">
        <!-- First row -->
        <vdr-form-field [label]="'Label'" class="form-item">
          <input type="text" [formControl]="labelControl" />
        </vdr-form-field>
        <vdr-form-field [label]="'Key'" class="form-item">
          <input type="text" [formControl]="keyControl" />
        </vdr-form-field>
      </div>

      <div class="form-row">
        <!-- Second row -->
        <vdr-form-field [label]="'Price'" class="form-item">
          <vdr-currency-input name="price" [currencyCode]="currencyCode" [formControl]="priceControl" />
        </vdr-form-field>
      </div>

      <div class="form-row" *ngIf="showImage">
        <!-- Third row -->
        <vdr-form-field [label]="'Image'" class="form-item">
          <vdr-dynamic-form-input
            [control]="imageControl"
            [def]="{
              type: 'relation',
              entity: 'Asset',
              ui: {
                component: 'relation-form-input',
              }
            }"
          >
          </vdr-dynamic-form-input>
        </vdr-form-field>
      </div>

      <div class="form-row">
        <!-- Fourth row - full width -->
        <vdr-form-field [label]="'Description'" class="form-item full-width">
          <textarea [formControl]="descriptionControl" rows="3"></textarea>
        </vdr-form-field>
      </div>
    </vdr-card>
  `,
  standalone: true,
  styleUrls: ['../../common/ui/styles.scss'],
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaidCustomizationItemFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;
  paidOptionItem: PaidOptionItem;

  // Form controls for each field
  keyControl = new FormControl();
  labelControl = new FormControl();
  priceControl = new FormControl();
  imageControl = new FormControl();
  descriptionControl = new FormControl();
  currencyCode: CurrencyCode;
  showImage = true;

  private destroy$ = new Subject<void>();

  constructor(
    private dataService: DataService,
    private changeDetector: ChangeDetectorRef,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.dataService.settings.getActiveChannel().single$.subscribe(data => {
        this.currencyCode = data.activeChannel.defaultCurrencyCode;
        this.changeDetector.markForCheck();
    });
    const options = this.config?.ui?.options ?? {};
    if (options && options.showImage$ instanceof BehaviorSubject) {
      const showImageBS = options.showImage$ as BehaviorSubject<boolean>;
      showImageBS.pipe(takeUntil(this.destroy$)).subscribe(val => {
        this.showImage = val;
        if (!val) {
          this.imageControl.setValue(null);
        }
        this.changeDetector.markForCheck();
      });
    } else {
      this.showImage = false;
    }
    const defaultPaidOptionItem: PaidOptionItem = {
      key: '',
      label: '',
      price: 0,
      assetId: null,
      description: ''
    };
    this.paidOptionItem = safeJSONParse<PaidOptionItem>(
      this.formControl.value,
      this.notificationService,
      defaultPaidOptionItem,
    )!;

    this.initializeFormControls();

    if (this.paidOptionItem?.assetId) {
      this.dataService.product
        .getAsset(this.paidOptionItem.assetId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.imageControl.setValue(asset);
        });
    }
  }

  ngAfterViewInit() {
    this.keyControl.valueChanges.subscribe(this.onKeyChange);
    this.labelControl.valueChanges.subscribe(this.onLabelChange);
    this.priceControl.valueChanges.subscribe(this.onPriceChange);
    this.imageControl.valueChanges.subscribe(this.onImageChange);
    this.descriptionControl.valueChanges.subscribe(this.onDescriptionChange);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormControls() {
    this.keyControl.setValue(this.paidOptionItem?.key);
    this.labelControl.setValue(this.paidOptionItem?.label);
    this.priceControl.setValue(this.paidOptionItem?.price);
    this.descriptionControl.setValue(this.paidOptionItem?.description);
  }

  private onKeyChange = (value: string) => {
    if (this.paidOptionItem?.key !== value) {
      this.paidOptionItem!.key = value;
      this.updateFormControlValue();
    }
  };

  private onLabelChange = (value: string) => {
    if (this.keyControl?.pristine) {
        this.keyControl.setValue(normalizeString(value || '', '-'));
    }
    if (this.paidOptionItem?.label !== value) {
      this.paidOptionItem!.label = value;
      this.updateFormControlValue();
    }
  };

  private onPriceChange = (value: number) => {
    if (this.paidOptionItem?.price !== value) {
      this.paidOptionItem!.price = value;
      this.updateFormControlValue();
    }
  };

  private onImageChange = (value: Asset | null) => {
    const assetId = value ? String(value.id) : null;
    const currentAssetId = this.paidOptionItem?.assetId != null ? String(this.paidOptionItem.assetId) : null;
    if (currentAssetId !== assetId) {
      this.paidOptionItem!.assetId = assetId;
      this.updateFormControlValue();
    }
  };

  private onDescriptionChange = (value: string) => {
    if (this.paidOptionItem?.description !== value) {
      this.paidOptionItem!.description = value;
      this.updateFormControlValue();
    }
  };

  private updateFormControlValue() {
    this.formControl.setValue(JSON.stringify(this.paidOptionItem));
    this.formControl.markAsDirty();
  }
}